import { fmtDate } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Navigation, Phone, Mail, CheckCircle2, Truck, MapPin, ArrowLeft, Package } from "lucide-react";
import { ProofModal } from "@/components/ProofOfDelivery";

const maps = (p) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${p.street || ""} ${p.plz || ""} ${p.ort || ""}, Deutschland`.trim())}&travelmode=driving`;
const dayLabel = (d) => {
  const today = new Date().toISOString().slice(0, 10);
  const tom = new Date(Date.now() + 864e5).toISOString().slice(0, 10);
  return d === today ? "Heute" : d === tom ? "Morgen" : d < today ? `Überfällig · ${fmtDate(d)}` : fmtDate(d);
};

export default function DriverView() {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [proof, setProof] = useState(null);
  const load = () => api.get("/partner/driver-plan").then((r) => setPlan(r.data)).catch(() => setPlan([]));
  useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line

  const setStatus = async (id, status) => {
    try { await api.post(`/bookings/${id}/transport-status?status=${status}`); toast.success(status === "zugestellt" ? "Zugestellt ✔" : "Unterwegs"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!user) return <div className="eb-container py-16 text-center"><Link to="/login" className="eb-btn-primary">Anmelden</Link></div>;
  if (!plan) return <div className="eb-container py-16 text-slate-400">Lädt…</div>;
  const open = plan.filter((b) => b.transport_status !== "zugestellt");
  const done = plan.filter((b) => b.transport_status === "zugestellt");
  const groups = open.reduce((acc, b) => { const d = (b.abfahrt || "").slice(0, 10); (acc[d] = acc[d] || []).push(b); return acc; }, {});

  return (
    <div className="eb-container max-w-2xl py-6 pb-24" data-testid="driver-view">
      {proof && <ProofModal booking={proof} onClose={() => setProof(null)} onDone={() => { setProof(null); load(); }} />}
      <div className="flex items-center justify-between mb-5">
        <Link to="/partner" className="eb-btn-ghost !px-2" data-testid="driver-back"><ArrowLeft className="w-4 h-4" /></Link>
        <h1 className="font-head text-xl font-bold flex items-center gap-2"><Truck className="w-5 h-5 text-[#FF5A1F]" /> Fahrer-Ansicht</h1>
        <span className="text-xs text-slate-500">{open.length} offen</span>
      </div>
      {open.length === 0 && <div className="eb-card p-8 text-center text-slate-500" data-testid="driver-empty">Keine offenen Transporte.</div>}
      {Object.entries(groups).map(([day, items]) => (
        <section key={day} className="mb-6" data-testid={`driver-day-${day}`}>
          <h2 className="eb-label mb-2 text-[#FF5A1F]">{dayLabel(day)} · {items.length} Stopp(s)</h2>
          <div className="space-y-3">
            {items.map((b) => (
              <div key={b.id} className="eb-card p-4" data-testid={`driver-job-${b.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-head font-semibold">{b.route_label}</div>
                  <span className={`eb-badge ${b.transport_status === "unterwegs" ? "bg-[#FF5A1F]/20 text-[#FF8A5F]" : "bg-slate-700 text-slate-300"}`}>{b.transport_status}</span>
                </div>
                <div className="text-xs text-slate-400 mt-1 flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> {b.volume_m3} m³ · {b.cargo_type}{b.services?.length ? ` · ${b.services.join(", ")}` : ""} · #{b.id.slice(0, 8).toUpperCase()}</div>
                <div className="grid sm:grid-cols-2 gap-2 mt-3">
                  {[["Abholung", b.pickup], ["Zustellung", b.dropoff]].map(([l, p]) => (
                    <a key={l} href={maps(p)} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-800 bg-[#0B0F17] p-3 hover:border-[#FF5A1F] transition-colors" data-testid={`driver-nav-${l.toLowerCase()}-${b.id}`}>
                      <div className="eb-label flex items-center justify-between">{l} <Navigation className="w-3.5 h-3.5 text-[#FF5A1F]" /></div>
                      <div className="text-sm mt-1 flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-500 shrink-0" /><span>{p.street ? `${p.street}, ` : ""}{p.plz} {p.ort}{p.access ? ` · ${p.access}` : ""}</span></div>
                      {p.options?.length > 0 && <div className="text-[11px] text-amber-300 mt-1">{p.options.join(" · ")}</div>}
                    </a>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {b.customer?.phone && <a href={`tel:${b.customer.phone}`} className="eb-btn-outline !py-2 text-sm" data-testid={`driver-call-${b.id}`}><Phone className="w-4 h-4" /> {b.customer.name || "Kunde"}</a>}
                  {b.customer?.email && <a href={`mailto:${b.customer.email}`} className="eb-btn-ghost !py-2 text-sm" data-testid={`driver-mail-${b.id}`}><Mail className="w-4 h-4" /></a>}
                  <div className="ml-auto flex gap-2">
                    {b.transport_status !== "unterwegs" && <button onClick={() => setStatus(b.id, "unterwegs")} className="eb-btn-outline !py-2 text-sm" data-testid={`driver-status-unterwegs-${b.id}`}><Truck className="w-4 h-4" /> Unterwegs</button>}
                    <button onClick={() => setProof(b)} className="eb-btn-primary !py-2 text-sm" data-testid={`driver-status-zugestellt-${b.id}`}><CheckCircle2 className="w-4 h-4" /> Zugestellt</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
      {done.length > 0 && (
        <details className="mt-6" data-testid="driver-done">
          <summary className="eb-label cursor-pointer">Erledigt ({done.length})</summary>
          <div className="space-y-2 mt-2">{done.map((b) => <div key={b.id} className="eb-card p-3 text-sm flex justify-between text-slate-400"><span>{b.route_label}</span><span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> zugestellt</span></div>)}</div>
        </details>
      )}
    </div>
  );
}
